import { addNumberSeparators } from '@Sage/ssm-utils';

import { subscribe } from 'lib/EventBuser';
import { DATA_GRID_REFRESH_TOTAL } from 'lib/events';
import { getHeaderSortField, isHeaderSortingEnabled } from '../../../utils/agGridColumn';

import CellCheckbox from '../HeaderCheckbox';
import HeaderDrag from '../HeaderDrag';
import HeaderSort from '../HeaderSort';

const normalizeTotal = (total) =>
    typeof total === 'number' && Number.isFinite(total) ? total : Number(total) || 0;

const formatHeaderTotal = (total, locale) => {
    const normalized = normalizeTotal(total);
    return normalized > 0 ? `(${addNumberSeparators(normalized, locale)})` : '';
};

const bindHeaderTotal = ({ totalDiv, context, locale, leftBlock, baseHeaderCell }) => {
    let displayedText = formatHeaderTotal(context?.stableHeaderTotal ?? context?.totalData, locale);

    if (displayedText) {
        totalDiv.textContent = displayedText;
    }

    const applyTotal = (total) => {
        const normalized = normalizeTotal(total);
        const nextText = formatHeaderTotal(normalized, locale);
        const isLoading = context?.isGridLoading;

        if (nextText) {
            if (displayedText !== nextText) {
                displayedText = nextText;
                totalDiv.textContent = nextText;
            }
        } else if (!isLoading && displayedText) {
            displayedText = '';
            totalDiv.textContent = '';
        }

        if (leftBlock) {
            if (!normalized && !isLoading) {
                baseHeaderCell.classList.add('w5-header-cell--checkbox-disabled');
            } else {
                baseHeaderCell.classList.remove('w5-header-cell--checkbox-disabled');
            }
        }
    };

    subscribe(`${DATA_GRID_REFRESH_TOTAL}-${context?.tableId}`, applyTotal);
};

export default function HeaderBaseCell(cell, params) {
    if (!params) return null;
    const baseHeaderCell = document.createElement('div');
    baseHeaderCell.classList.add('w5-header-cell');
    let centerBlock = null;
    let leftBlock = null;

    if (params.useDragColumns || params?.checkbox?.onClick) {
        leftBlock = document.createElement('div');
        leftBlock.classList.add('w5-header-cell-left');
    }

    const finalHeaderCell = cell;
    if (params.useDragColumns) {
        const drag = HeaderDrag(params);
        leftBlock.appendChild(drag);
        baseHeaderCell.classList.add('w5-header-cell--flex');
    }

    if (params?.checkbox?.onClick) {
        const checkbox = CellCheckbox(params);
        checkbox && leftBlock.appendChild(checkbox);
        baseHeaderCell.classList.add('w5-header-cell--flex');
    }

    if (finalHeaderCell) {
        centerBlock = document.createElement('div');
        centerBlock.classList.add('w5-header-cell-center');
        centerBlock.appendChild(finalHeaderCell);
        baseHeaderCell.classList.add('w5-header-cell--flex');
    }

    if (centerBlock && params.showTotal) {
        const totalDiv = document.createElement('div');
        totalDiv.classList.add('w5-header-cell-total');
        const locale = params?.context?.locale || '';
        centerBlock.appendChild(totalDiv);
        baseHeaderCell.classList.add('w5-header-cell--flex');
        bindHeaderTotal({
            totalDiv,
            context: params.context,
            locale,
            leftBlock,
            baseHeaderCell,
        });
    }

    if (centerBlock && isHeaderSortingEnabled(params)) {
        const sort = HeaderSort(params);
        centerBlock.appendChild(sort);
        baseHeaderCell.classList.add('w5-header-cell--flex');

        const sortingDirection = ['none', 'asc', 'desc'];
        let currentSortDirection = sort.getAttribute('data-sort') || 'none';
        sort.setAttribute('data-sort', currentSortDirection);
        setInitialSortDirection(sort, params, currentSortDirection);
        addSortHandler(baseHeaderCell, sort, params, sortingDirection);
        baseHeaderCell.classList.add('w5-header-cell--sortable');
    }

    if (leftBlock) {
        baseHeaderCell.appendChild(leftBlock);
        subscribe(`${DATA_GRID_REFRESH_TOTAL}-${params?.context?.tableId}`, (total) => {
            const normalized = normalizeTotal(total);
            if (!normalized && !params?.context?.isGridLoading) {
                baseHeaderCell.classList.add('w5-header-cell--checkbox-disabled');
            } else {
                baseHeaderCell.classList.remove('w5-header-cell--checkbox-disabled');
            }
        });
    }
    if (centerBlock) baseHeaderCell.appendChild(centerBlock);

    return baseHeaderCell;
}

const getSortDirectionClass = (sortDirection, element) => {
    if (sortDirection === 'asc') {
        element.classList.remove('w5-header-sort--hidden');
        element.classList.remove('w5-header-sort--desc');
        element.classList.add('w5-header-sort--asc');
    } else if (sortDirection === 'desc') {
        element.classList.remove('w5-header-sort--hidden');
        element.classList.remove('w5-header-sort--asc');
        element.classList.add('w5-header-sort--desc');
    } else {
        element.classList.remove('w5-header-sort--asc');
        element.classList.remove('w5-header-sort--desc');
        element.classList.add('w5-header-sort--hidden');
    }
};

const setInitialSortDirection = (sortElement, params, currentSortDirection) => {
    if (params.getInitialSortInfo) {
        // The table it's initialized with sortDir as null. We use getInitialSortInfo to obtain
        // >> the real sort direction of the data displayed in order to display the arrow correctly.
        const initialSortInfo = params.getInitialSortInfo();
        const sortField = getHeaderSortField(params);
        if (sortField && sortField === initialSortInfo?.sortField) {
            currentSortDirection =
                !initialSortInfo || initialSortInfo?.sortDirection === null
                    ? 'none'
                    : initialSortInfo.sortDirection;
            getSortDirectionClass(currentSortDirection, sortElement);
            sortElement.setAttribute('data-sort', currentSortDirection);
            if (params.getSortInfoForLazyLoad) {
                // This is to give feedback about the sort info in the first load
                // We don't use setSortModel because gave problems for the lazyLoad specific case.
                const sortInfo = {
                    colId: params.column.colId,
                    sortField,
                    sort: currentSortDirection === 'none' ? null : currentSortDirection,
                };
                params.getSortInfoForLazyLoad(sortInfo);
            }
        }
    }
};

const getOtherColumnsSortState = (currentColumnId, params) => {
    return (params.api?.getColumnState?.() || []).reduce((states, column) => {
        if (column.colId !== currentColumnId && column.sort != null) {
            states.push({ colId: column.colId, sort: null });
        }
        return states;
    }, []);
};

const resetOtherColumnsSort = (currentColumnId, anchorEl, params) => {
    // AG Grid v33+ removed the internal gridPanel; resolve this grid's root from the
    // clicked header cell (preferred) or fall back to the table id.
    const tableRoot =
        anchorEl?.closest?.('.ag-root-wrapper') ||
        (params?.context?.tableId ? document.getElementById(params.context.tableId) : null);
    const columns = tableRoot?.querySelectorAll('.ag-header-cell') || null;
    if (!columns) return;
    for (let column of columns) {
        const colId = column.getAttribute('col-id');
        if (currentColumnId !== colId) {
            const sortElement = column.querySelector('.w5-header-sort');
            if (!sortElement) continue;
            sortElement.classList.remove('w5-header-sort--asc');
            sortElement.classList.remove('w5-header-sort--desc');
            sortElement.classList.add('w5-header-sort--hidden');
            sortElement.setAttribute('data-sort', 'none');
        }
    }
};

const addSortHandler = (baseHeaderCell, sortElement, params, sortingDirection) => {
    baseHeaderCell.addEventListener('click', function (event) {
        event.stopPropagation();

        const sortField = getHeaderSortField(params);
        const currentSortDirection = sortElement.getAttribute('data-sort');
        let sortDirectionPosition = sortingDirection.indexOf(currentSortDirection);
        sortDirectionPosition =
            sortDirectionPosition + 1 > sortingDirection.length - 1 ? 0 : sortDirectionPosition + 1;

        let newSortDirection = sortingDirection[sortDirectionPosition] || sortingDirection[0];
        const columnSortState = {
            colId: params.column.colId,
            sort: newSortDirection === 'none' ? null : newSortDirection,
        };
        const sortState = [
            columnSortState,
            ...getOtherColumnsSortState(params.column.colId, params),
        ];

        if (params.changeSortDirection && sortField) {
            // We sort the column manually for those tables that use backend sorting
            // >> but don't use lazyLoad.
            params.changeSortDirection(sortField);
        } else if (params.getSortInfoForLazyLoad) {
            // We inform about the next sort direction that should be applied.
            // The sort arrow direction is displayed according this parameters.
            // The lazy load should use this parameters to make the call in order to match the
            // >> the data sort direction with the arrow direction.
            const sortModel = {
                ...columnSortState,
                sortField,
            };

            params.getSortInfoForLazyLoad(sortModel);
            // setSortModel triggers the getRows method of the table dataSource allowing table
            // >> to update de rows.

            params.api.applyColumnState({ state: sortState });
        } else {
            // This controls the native sorting of the table
            // Setting the sortModel we are telling the table how to sort the column with
            // >> its inner controls.
            params.api.applyColumnState({ state: sortState });
        }
        resetOtherColumnsSort(params.column.colId, baseHeaderCell, params);
        getSortDirectionClass(newSortDirection, sortElement);

        sortElement.setAttribute('data-sort', newSortDirection);
    });
};
